import React, { useState } from 'react';
import { Info } from 'lucide-react';
import { palette, components } from '../utils/designTokens';
import { formatDuration, formatTokens, formatCost, formatLocalTime } from '../utils/formatters';
import { getOutcomeLabel, getOutcomePillStyle, getOutcomeReasonLabel } from '../utils/outcomeUtils';
import CopyButton from './CopyButton';

// hint: optional extra detail shown on hover. Renders a small info icon so the
// affordance is visible instead of relying on an undiscoverable bare tooltip.
const SummaryPill = ({ label, value, valueClassName = 'text-text-primary font-mono', valueStyle = undefined, hint = null }) => (
  <div
    className={`${components.badge.padding} ${components.badge.rounded} ${components.badge.size} ${components.badge.weight}`}
    style={{
      backgroundColor: palette.neutrals.panelTertiary,
      color: palette.text.primary,
      border: `1px solid ${palette.borders.subtle}`,
      cursor: hint ? 'help' : undefined,
    }}
    title={hint || undefined}
  >
    <span className="text-text-supporting">{label}</span>
    <span className={`ml-2 ${valueClassName}`} style={valueStyle}>{value ?? '—'}</span>
    {hint && (
      <Info size={10} className="ml-1 inline-block align-middle" style={{ color: palette.text.muted }} />
    )}
  </div>
);

export { SummaryPill };

export default function TraceSummaryBar({ trace }) {
  const [expanded, setExpanded] = useState(false);

  if (!trace) return null;

  const wf = trace.workflow || {};
  const workflowName = wf.workflow_name || trace.workflow_name || null;
  const workflowFields = [
    { label: 'Business Task',    value: wf.workflow_name    || trace.workflow_name },
  ].filter(f => f.value);
  const hasWorkflow = workflowFields.length > 0;
  const rawOutcome = wf.outcome || '';
  const outcomeReason = getOutcomeReasonLabel(wf.outcome_reason);

  const hasTokenBreakdown = trace.total_input_tokens != null
    || trace.total_output_tokens != null
    || trace.total_cached_tokens != null;
  const tokenBreakdown = hasTokenBreakdown
    ? `In: ${formatTokens(trace.total_input_tokens)} · Out: ${formatTokens(trace.total_output_tokens)} · Cached: ${formatTokens(trace.total_cached_tokens)}`
    : null;

  const models = Array.isArray(trace.models) ? trace.models : [];
  const modelLabel = models.length === 0
    ? '—'
    : models.length === 1
      ? models[0]
      : `${models[0]} +${models.length - 1}`;

  return (
    <div className="rounded-lg border border-border-subtle bg-background-secondary">
      {/* Single combined row */}
      <div className="flex flex-wrap items-center gap-3 px-3 py-2">

        {/* Business task toggle — left side */}
        {hasWorkflow && (
          <>
            <button
              type="button"
              className="flex items-center gap-2 text-left shrink-0"
              onClick={() => setExpanded(v => !v)}
            >
              <span style={{ fontSize: 10, color: 'var(--color-text-muted)', userSelect: 'none' }}>
                {expanded ? '▲' : '▼'}
              </span>
              <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-text-primary)' }}>
                Business Task
              </span>
              <span style={{ fontSize: 12, color: palette.accents.primary.base }}>
                {workflowName}
              </span>
              <span
                className="rounded-full"
                style={{ ...getOutcomePillStyle(rawOutcome), fontSize: 10, fontWeight: 600, padding: '1px 7px' }}
              >
                {getOutcomeLabel(rawOutcome)}
              </span>
              {outcomeReason && (
                <span
                  className="truncate"
                  style={{ maxWidth: 360, fontSize: 11, color: palette.text.muted }}
                  title={outcomeReason}
                >
                  {outcomeReason}
                </span>
              )}
            </button>

            {/* Vertical divider */}
            <div className="h-4 w-px shrink-0" style={{ background: palette.borders.subtle }} />
          </>
        )}

        {/* Trace metrics pills */}
        <div className="flex flex-wrap items-center gap-2 flex-1 lg:justify-between">
          <div className="flex flex-wrap gap-2">
            <div className="flex items-center gap-1">
              <SummaryPill label="Trace" value={trace.trace_id?.slice(0, 8)} />
              <CopyButton text={trace.trace_id} size="xs" label="Copy Trace ID" />
            </div>
            <SummaryPill label="Spans"   value={trace.span_count ?? '—'} />
            <SummaryPill label="Latency" value={formatDuration(trace.duration_ms)} />
            <SummaryPill label="Tokens"  value={formatTokens(trace.tokens || 0)} hint={tokenBreakdown} />
            <SummaryPill label="Cost"    value={formatCost(trace.cost)} />
            <SummaryPill label="Errors"  value={trace.errors || 0} />
            {trace.tool_status && (
              <SummaryPill label="Tools" value={`${trace.tool_status.total_calls || 0}${trace.tool_status.issues ? ` (${trace.tool_status.issues} issues)` : ''}`} />
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            <SummaryPill label="Model" value={modelLabel} />
            <SummaryPill label="Start" value={formatLocalTime(trace.trace_start)} />
          </div>
        </div>
      </div>

      {/* Expanded business task details */}
      {hasWorkflow && expanded && (
        <div
          className="border-t border-border-subtle px-4 py-3"
          style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '10px 24px' }}
        >
          {workflowFields.map(({ label, value }) => (
            <div key={label} className="flex flex-col" style={{ gap: 2 }}>
              <span style={{ fontSize: 10, color: 'var(--color-text-muted)', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                {label}
              </span>
              <span style={{ fontSize: 12, color: 'var(--color-text-primary)', lineHeight: 1.4 }}>
                {value}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
